// src/hooks/useDocumentCompression.js
import { useState, useCallback } from "react";
import {
  validateAndCompressDocument,
  formatFileSize,
} from "../utils/documentCompression";

/**
 * Custom hook for document compression
 * Supports both single and multiple document compression workflows
 * @returns {Object} Compression state and handlers
 */
const useDocumentCompression = () => {
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Compress a single document file
   * @param {File} file - Document file to compress
   * @param {number} maxSizeMB - Target max size in MB (default: 30)
   * @returns {Promise<Object>} Compression result with metadata
   */
  const compressSingleDocument = useCallback(async (file, maxSizeMB = 30) => {
    try {
      setIsCompressing(true);
      setError(null);

      setCompressionProgress({
        fileName: file.name,
        originalSize: formatFileSize(file.size),
        status: "compressing",
      });

      const result = await validateAndCompressDocument(file, maxSizeMB);

      setCompressionProgress({
        fileName: file.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.size),
        compressionRatio: result.compressionRatio,
        status: "complete",
      });

      console.log(
        `📄 "${file.name}": ${formatFileSize(result.originalSize)} → ${formatFileSize(result.size)} (${result.compressionRatio}% compression)`,
      );

      return result;
    } catch (err) {
      console.error("Error compressing document:", err);
      setError(err.message || "Failed to compress document");
      setCompressionProgress({
        fileName: file.name,
        status: "error",
        error: err.message,
      });
      throw err;
    } finally {
      setIsCompressing(false);
    }
  }, []);

  /**
   * Compress multiple documents sequentially
   * @param {FileList|File[]} files - Array of document files
   * @param {number} maxSizeMB - Target max size in MB (default: 30)
   * @returns {Promise<Object[]>} Array of compressed documents
   */
  const compressMultipleDocuments = useCallback(
    async (files, maxSizeMB = 30) => {
      try {
        setIsCompressing(true);
        setError(null);

        const results = [];
        const fileArray = Array.from(files);

        for (let i = 0; i < fileArray.length; i++) {
          const file = fileArray[i];

          // Update progress
          setCompressionProgress({
            current: i + 1,
            total: fileArray.length,
            fileName: file.name,
            status: "compressing",
          });

          try {
            const result = await validateAndCompressDocument(file, maxSizeMB);
            results.push(result);

            console.log(
              `📄 [${i + 1}/${fileArray.length}] "${file.name}": ${formatFileSize(result.originalSize)} → ${formatFileSize(result.size)} (${result.compressionRatio}% compression)`,
            );
          } catch (err) {
            console.error(`Failed to compress ${file.name}:`, err);
            setError(`Failed to compress ${file.name}: ${err.message}`);
            // Continue with next file instead of stopping
          }
        }

        setCompressionProgress({
          status: "complete",
          totalFiles: fileArray.length,
          successCount: results.length,
        });

        return results;
      } catch (err) {
        console.error("Error compressing multiple documents:", err);
        setError(err.message || "Failed to compress documents");
        throw err;
      } finally {
        setIsCompressing(false);
      }
    },
    [],
  );

  /**
   * Reset compression state
   */
  const reset = useCallback(() => {
    setIsCompressing(false);
    setCompressionProgress(null);
    setError(null);
  }, []);

  return {
    isCompressing,
    compressionProgress,
    error,
    compressSingleDocument,
    compressMultipleDocuments,
    reset,
  };
};

export default useDocumentCompression;

// 30
